import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faGraduationCap, faCog } from '@/fontawesome';
import { formatCurrencyCentValue, toCurrencyCentValue } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import { showToast } from '@/utils/toasts';
import ToggleButton from '@/components/ui/ToggleButton';
import type {User, FeatureConfig, Course, CourseID, FeatureConfigKey} from '@/models';

interface FeatureConfigCardProps {
  user: User;
  onUserUpdated: (updatedUser: User) => void;
}

export default function FeatureConfigCard({ user, onUserUpdated }: FeatureConfigCardProps) {
  const [manualFeatureConfig, setManualFeatureConfig] = useState<FeatureConfig>(user.manualFeatureConfig);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    setManualFeatureConfig(user.manualFeatureConfig);
    setHasChanges(false);
  }, [user]);

  useEffect(() => {
    setHasChanges(JSON.stringify(manualFeatureConfig) !== JSON.stringify(user.manualFeatureConfig));
  }, [manualFeatureConfig, user.manualFeatureConfig]);

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const response = await APIRoutes.listCourses({ limit: PAGINATION.FETCH_ALL_LIMIT });
      setCourses(response.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      showToast('Fehler beim Laden der Kurse', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updateRequest = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        permissionGroup: user.permissionGroup,
        manualFeatureConfig: manualFeatureConfig
      };

      const updatedUser = await APIRoutes.updateUser(user.id, updateRequest);
      onUserUpdated(updatedUser);
      showToast('Feature-Konfiguration erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating feature config:', error);
      showToast('Fehler beim Aktualisieren der Feature-Konfiguration', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (field: FeatureConfigKey) => {
    setManualFeatureConfig(prev => ({
      ...prev,
      [field]: !prev[field as FeatureConfigKey]
    }));
  };

  const handleLimitChange = (field: 'coreRecommendationStrikeLimit' | 'earningRecommendationStrikeLimit' | 'swingTradeRecommendationPriceLimit' | 'longTermRecommendationPriceLimit', value: string) => {
    const numValue = parseFloat(value);
    setManualFeatureConfig(prev => ({
      ...prev,
      [field]: value === '' ? null : toCurrencyCentValue(numValue)
    }));
  };

  const handleCourseToggle = (courseId: CourseID) => {
    setManualFeatureConfig(prev => ({
      ...prev,
      courses: {
        ...prev.courses,
        [courseId]: !prev.courses[courseId]
      }
    }));
  };


  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FontAwesomeIcon icon={faCog} className="w-6 h-6 mr-3" />
          Manuelle Feature-Konfiguration
        </h2>
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center ${
            hasChanges ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {isSaving && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />}
          Speichern
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto p-2">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                  Aktiviert
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Feature
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Limit (USD)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Core Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.coreRecommendations}
                    onClick={() => handleToggle('coreRecommendations')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.coreRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Core-Tradingideen
                  </div>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.coreRecommendationStrikeLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.coreRecommendationStrikeLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('coreRecommendationStrikeLimit', e.target.value)}
                    disabled={isSaving || !manualFeatureConfig.coreRecommendations}
                    placeholder="Kein Limit"
                    className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                  />
                </td>
              </tr>

              {/* Earning Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.earningRecommendations}
                    onClick={() => handleToggle('earningRecommendations')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.earningRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Earnings-Tradingideen
                  </div>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.earningRecommendationStrikeLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.earningRecommendationStrikeLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('earningRecommendationStrikeLimit', e.target.value)}
                    disabled={isSaving || !manualFeatureConfig.earningRecommendations}
                    placeholder="Kein Limit"
                    className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                  />
                </td>
              </tr>

              {/* Swing Trade Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.swingTradeRecommendations}
                    onClick={() => handleToggle('swingTradeRecommendations')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.swingTradeRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Swing-Tradingideen
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Startpreis-Limit</div>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.swingTradeRecommendationPriceLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.swingTradeRecommendationPriceLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('swingTradeRecommendationPriceLimit', e.target.value)}
                    disabled={isSaving || !manualFeatureConfig.swingTradeRecommendations}
                    placeholder="Kein Limit"
                    className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                  />
                </td>
              </tr>

              {/* Long Term Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.longTermRecommendations}
                    onClick={() => handleToggle('longTermRecommendations')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.longTermRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Langzeit-Tradingideen
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Startpreis-Limit</div>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.longTermRecommendationPriceLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.longTermRecommendationPriceLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('longTermRecommendationPriceLimit', e.target.value)}
                    disabled={isSaving || !manualFeatureConfig.longTermRecommendations}
                    placeholder="Kein Limit"
                    className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                  />
                </td>
              </tr>

              {/* Boost Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.boostRecommendations}
                    onClick={() => handleToggle('boostRecommendations')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.boostRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Boost-Tradingideen
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                </td>
              </tr>

              {/* Community Member */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.communityMember}
                    onClick={() => handleToggle('communityMember')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.communityMember ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Community-Mitglied
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Chatbot */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.chatbot}
                    onClick={() => handleToggle('chatbot')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.chatbot ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Chatbot
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Video Chatbot */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.videoChatbot}
                    onClick={() => handleToggle('videoChatbot')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.videoChatbot ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Video-Chatbot
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Journal */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.journal}
                    onClick={() => handleToggle('journal')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.journal ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Journal
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Daytrading */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.dayTrading}
                    onClick={() => handleToggle('dayTrading')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.dayTrading ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Daytrading
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Options-Screener */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.optionScreener}
                    onClick={() => handleToggle('optionScreener')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.optionScreener ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Options-Screener
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Video-Finder */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.videoSearch}
                    onClick={() => handleToggle('videoSearch')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${manualFeatureConfig.videoSearch ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Video-Finder
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* All Courses */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleButton
                    enabled={manualFeatureConfig.allCourses}
                    onClick={() => handleToggle('allCourses')}
                    disabled={isSaving}
                    size="lg"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium flex items-center ${manualFeatureConfig.allCourses ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 mr-2" />
                    Alle Kurse
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Individual Courses */}
              {courses.map(course => (
                <tr key={course.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ToggleButton
                      enabled={manualFeatureConfig.courses[course.id] ?? false}
                      onClick={() => handleCourseToggle(course.id)}
                      disabled={isSaving || manualFeatureConfig.allCourses}
                      size="md"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm font-medium flex items-center ${
                      manualFeatureConfig.allCourses
                        ? 'text-gray-400 dark:text-gray-600'
                        : (manualFeatureConfig.courses[course.id] ?? false)
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-gray-600'
                    }`}>
                      <span className="ml-8">{course.title}</span>
                      {!course.published && (
                        <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400">
                          Entwurf
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
